import { isNative } from 'src/utils/platform';
import { env } from 'src/config/env';
import { useCallback, useEffect, useRef, FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import {
  clearNotificationClickUrlHandler,
  setNotificationClickUrlHandler,
} from 'src/modules/push-notifications/Onesignal';
import { setDeepLinkHandler } from 'src/modules/AppsFlyer/AppsFlyer.module';
import { resolveDeepLink } from 'src/router/deepLink';

const AppUrlListener: FunctionComponent = () => {
  const navigate = useNavigate();

  const processUrl = useCallback(
    (url: string) => {
      const target = resolveDeepLink(url, window.location.search);
      if (target) navigate(target);
    },
    [navigate],
  );

  const processUrlRef = useRef(processUrl);
  useEffect(() => {
    processUrlRef.current = processUrl;
  }, [processUrl]);

  useEffect(() => {
    const handleUrl = (url: string) => processUrlRef.current(url);
    const handle = App.addListener(
      'appUrlOpen',
      (event: URLOpenListenerEvent) => handleUrl(event.url),
    ).catch(() => undefined);

    if (env.isDev || env.mode === 'test') {
      (window as Window & { __triggerAppUrlOpen?: (url: string) => void })
        .__triggerAppUrlOpen = handleUrl;
    }

    const isNativeApp = isNative();
    if (isNativeApp) {
      setDeepLinkHandler(handleUrl);
      setNotificationClickUrlHandler(handleUrl);
    }

    return () => {
      void handle.then(resolved => resolved?.remove());
      if (isNativeApp) {
        clearNotificationClickUrlHandler(handleUrl);
      }
      if (env.isDev || env.mode === 'test') {
        delete (window as Window & { __triggerAppUrlOpen?: (url: string) => void })
          .__triggerAppUrlOpen;
      }
    };
  }, []);

  return null;
};

export default AppUrlListener;
